import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap",
    "font-medium transition-all duration-150",
    "focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2",
    "disabled:opacity-40 disabled:cursor-not-allowed",
    "select-none",
  ].join(" "),
  {
    variants: {
      variant: {
        primary: [
          "bg-accent text-bg hover:bg-accent-bright",
          "shadow-[0_1px_0_rgba(255,255,255,0.15)_inset,0_8px_20px_-8px_rgba(230,204,128,0.5)]",
          "active:translate-y-px",
        ].join(" "),
        secondary: [
          "bg-surface-2 text-fg border border-border",
          "hover:bg-surface-3 hover:border-border-strong",
        ].join(" "),
        ghost: [
          "text-fg-muted hover:text-fg hover:bg-surface-2",
        ].join(" "),
        outline: [
          "border border-border-strong text-fg",
          "hover:bg-surface-2 hover:border-accent-dim",
        ].join(" "),
        battlenet: [
          // Melli-textured fill via the .btn-battlenet-melli class
          // defined in globals.css — vertical gradient + 1px bright top
          // strip + subtle dark bottom + glow halo.
          "btn-battlenet-melli text-white",
          "active:translate-y-px transition-transform",
        ].join(" "),
        danger: [
          "bg-danger/90 text-white hover:bg-danger",
        ].join(" "),
      },
      size: {
        sm: "h-8 px-3 text-xs rounded-sm",
        md: "h-10 px-4 text-sm rounded-sm",
        lg: "h-12 px-6 text-base rounded-md",
        icon: "h-9 w-9 rounded-sm",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { buttonVariants };
